
import React, { useState } from 'react';
import { RSVPFormData } from '../types';

// We just need the ID here, not the full URL
const FORMSPREE_ID = 'meeavebr'; 

type SubmissionStatus = 'idle' | 'submitting' | 'submitted' | 'error';

export const RSVP: React.FC = () => {
  const [formData, setFormData] = useState<RSVPFormData>({
    firstName: '',
    lastName: '',
    attending: 'yes',
    starter: '',
    mainMeal: '',
    dessert: '',
    dietary: '',
  });

  const [status, setStatus] = useState<SubmissionStatus>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('submitting');

    try {
      const response = await fetch(`https://formspree.io/f/${FORMSPREE_ID}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setStatus('submitted');
      } else {
        setStatus('error');
      }
    } catch (error) {
      console.error('Submission error:', error);
      setStatus('error');
    }
  };

  const starterOptions = [
    "Lentil Soup",
    "Chicken Liver Parfait, Enterkine Chutney & Arran Oatcakes",
    "Children's Starter - Lentil Soup",
    "Children's Starter - Fruit Platter",
    "Children's Starter - Garlic Bread"
  ];

  const mainOptions = [
    "Pan Seared Chicken Breast of Chicken, Garlic Mash, Roast Root Vegetables with White Wine & Leek Sauce",
    "Slow Cooked Daube of Beef with Buttered Mash, Roast Root Vegetables with a Red Wine Jus",
    "Vegetarian/Vegan Meal Option",
    "Children's Menu - Chicken Breast Goujons with Salad & Chips",
    "Children's Menu - Sausage, Mash & Gravy",
    "Children's Menu - Macaroni Cheese with Chips or Garlic Bread"
  ];

  const dessertOptions = [
    "Glazed Lemon Tart served with Red Berry Compote and Chantilly Cream",
    "Chef's Homemade Sticky Toffee Pudding",
    "Vegan Option",
    "Children's Menu - Chocolate Brownie",
    "Children's Menu - Sticky Toffee Pudding",
    "Children's Menu - Trio of Ice Creams"
  ];

  if (status === 'submitted') {
    return (
      <div className="py-24 max-w-xl mx-auto px-6 text-center bg-[#FDFBF7] rounded-lg animate-fade-in">
        <div className="mb-6 inline-block p-4 rounded-full bg-[#F5F2ED]">
          <svg className="w-12 h-12 text-[#8E806A]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h3 className="font-serif-display text-3xl mb-4 text-black">Thank You!</h3>
        <p className="text-gray-600 font-light mb-8">
          We have received your individual RSVP. We can't wait to see you there!
        </p>
        <button 
          onClick={() => {
            setStatus('idle');
            setFormData({ ...formData, firstName: '', lastName: '', attending: 'yes', starter: '', mainMeal: '', dessert: '', dietary: '' });
          }}
          className="text-sm font-medium tracking-[0.2em] text-[#8E806A] border-b border-[#8E806A]/30 pb-1"
        >
          SUBMIT ANOTHER RESPONSE FOR A GUEST
        </button>
      </div>
    );
  }

  return (
    <div className="py-24 max-w-3xl mx-auto px-6">
      <div className="bg-white p-8 md:p-12 shadow-[0_0_50px_rgba(0,0,0,0.05)] rounded-sm">
        <div className="text-center mb-12">
          <h2 className="font-serif-display text-4xl md:text-5xl mb-4">RSVP</h2>
          <p className="text-gray-400 italic">Please RSVP for each guest individually</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-8">
          <div className="grid md:grid-cols-2 gap-8">
            <div className="space-y-2">
              <label className="text-[10px] tracking-[0.2em] font-bold text-gray-500 uppercase">First Name</label>
              <input 
                required
                type="text" 
                className="w-full border-b border-gray-200 py-3 focus:outline-none focus:border-[#8E806A] transition-colors bg-transparent font-light"
                value={formData.firstName}
                onChange={(e) => setFormData({...formData, firstName: e.target.value})}
              />
            </div>
            <div className="space-y-2">
              <label className="text-[10px] tracking-[0.2em] font-bold text-gray-500 uppercase">Last Name</label>
              <input 
                required
                type="text" 
                className="w-full border-b border-gray-200 py-3 focus:outline-none focus:border-[#8E806A] transition-colors bg-transparent font-light"
                value={formData.lastName}
                onChange={(e) => setFormData({...formData, lastName: e.target.value})}
              />
            </div>
          </div>

          <div className="space-y-4">
            <label className="text-[10px] tracking-[0.2em] font-bold text-gray-500 uppercase block">Will you attend?</label>
            <div className="flex space-x-8">
              <label className="flex items-center space-x-3 cursor-pointer group">
                <input 
                  type="radio" 
                  name="attending" 
                  className="w-4 h-4 accent-[#8E806A]"
                  checked={formData.attending === 'yes'}
                  onChange={() => setFormData({...formData, attending: 'yes'})}
                />
                <span className="font-light text-gray-600 group-hover:text-[#8E806A] transition-colors">Yes, I'll be there</span>
              </label>
              <label className="flex items-center space-x-3 cursor-pointer group">
                <input 
                  type="radio" 
                  name="attending" 
                  className="w-4 h-4 accent-[#8E806A]"
                  checked={formData.attending === 'no'}
                  onChange={() => setFormData({...formData, attending: 'no'})}
                />
                <span className="font-light text-gray-600 group-hover:text-[#8E806A] transition-colors">Regretfully decline</span>
              </label>
            </div>
          </div>

          {formData.attending === 'yes' && (
            <div className="space-y-8 animate-fade-in">
              <div className="space-y-2">
                <label className="text-[10px] tracking-[0.2em] font-bold text-gray-500 uppercase">Starter Choice</label>
                <select 
                  required
                  className="w-full border-b border-gray-200 py-3 focus:outline-none focus:border-[#8E806A] transition-colors bg-transparent font-light cursor-pointer"
                  value={formData.starter}
                  onChange={(e) => setFormData({...formData, starter: e.target.value})}
                >
                  <option value="" disabled>Select your starter</option>
                  {starterOptions.map(option => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </select>
              </div>

              <div className="space-y-2">
                <label className="text-[10px] tracking-[0.2em] font-bold text-gray-500 uppercase">Main Meal Choice</label>
                <select 
                  required
                  className="w-full border-b border-gray-200 py-3 focus:outline-none focus:border-[#8E806A] transition-colors bg-transparent font-light cursor-pointer"
                  value={formData.mainMeal}
                  onChange={(e) => setFormData({...formData, mainMeal: e.target.value})}
                >
                  <option value="" disabled>Select your main meal</option>
                  {mainOptions.map(option => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </select>
              </div>

              <div className="space-y-2">
                <label className="text-[10px] tracking-[0.2em] font-bold text-gray-500 uppercase">Dessert Choice</label>
                <select 
                  required
                  className="w-full border-b border-gray-200 py-3 focus:outline-none focus:border-[#8E806A] transition-colors bg-transparent font-light cursor-pointer"
                  value={formData.dessert}
                  onChange={(e) => setFormData({...formData, dessert: e.target.value})}
                >
                  <option value="" disabled>Select your dessert</option>
                  {dessertOptions.map(option => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </select>
              </div>

              <div className="space-y-2">
                <label className="text-[10px] tracking-[0.2em] font-bold text-gray-500 uppercase">Dietary Requirements</label>
                <textarea 
                  rows={2}
                  placeholder="Allergies, vegetarian, vegan, etc."
                  className="w-full border-b border-gray-200 py-3 focus:outline-none focus:border-[#8E806A] transition-colors bg-transparent font-light resize-none"
                  value={formData.dietary}
                  onChange={(e) => setFormData({...formData, dietary: e.target.value})}
                />
              </div>
            </div>
          )}

          {status === 'error' && (
            <p className="text-red-500 text-xs text-center">
              There was an error sending your response. Please try again later.
            </p>
          )}

          <div className="pt-6">
            <button 
              type="submit"
              disabled={status === 'submitting'}
              className="w-full bg-black text-white py-5 px-8 text-xs tracking-[0.4em] font-bold uppercase hover:bg-black/80 transition-all duration-300 shadow-xl disabled:bg-gray-400"
            >
              {status === 'submitting' ? 'Sending...' : 'Send Response'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
